import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Package, Truck, ClipboardList, Building2, Store,
  Users2, ShieldCheck, BadgePercent, Menu, X, LogOut, Wallet, SearchCheck, Route,
  Bike, Car, Coins, FileText, CreditCard, BadgeDollarSign, PlusCircle,
  PackageOpen, AlertTriangle, BarChart3, Settings, UserRound, KeyRound,
} from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/format";

const STAFF = ["admin", "encargado", "encargado_comisionista"];
const OPS = ["admin", "encargado", "encargado_comisionista", "punto"];
const ADMIN_PUNTO = ["admin", "punto"];

const ALL_ITEMS = [
  { to: "/app", label: "Panel", icon: LayoutDashboard, roles: "*", end: true },
  { to: "/app/mi-ruta", label: "Mi Ruta", icon: Route, roles: ["admin", "encargado", "encargado_comisionista", "repartidor", "comisionista"] },
  { to: "/app/usuarios", label: "Usuarios y Roles", icon: Users2, roles: ["admin"] },
  { to: "/app/aprobaciones", label: "Aprobaciones", icon: UserRound, roles: ["admin"] },
  { to: "/app/clientes", label: "Clientes", icon: UserRound, roles: ADMIN_PUNTO },
  { to: "/app/solicitudes", label: "Solicitudes", icon: ClipboardList, roles: ADMIN_PUNTO },
  { to: "/app/sucursales", label: "Sucursales", icon: Building2, roles: ["admin"] },
  { to: "/app/gestionar-sucursal", label: "Gestionar sucursal", icon: Store, roles: OPS },
  { to: "/app/envios", label: "Envíos", icon: Package, roles: ["admin", "encargado", "encargado_comisionista", "punto", "cliente"] },
  { to: "/app/retiros", label: "Retiros", icon: Truck, roles: ["admin", "encargado", "encargado_comisionista", "punto", "cliente"] },
  { to: "/app/tracking", label: "Tracking", icon: SearchCheck, roles: "*" },
  { to: "/app/repartidores", label: "Repartidores", icon: Bike, roles: ["admin"] },
  { to: "/app/vehiculos", label: "Vehículos", icon: Car, roles: ["admin"] },
  { to: "/app/cod", label: "COD", icon: Coins, roles: ["admin"] },
  { to: "/app/liquidaciones", label: "Liquidaciones", icon: FileText, roles: ["admin"] },
  { to: "/app/mi-liquidacion", label: "Mi liquidación", icon: Coins, roles: ["cliente"] },
  { to: "/app/rendiciones", label: "Rendiciones", icon: Wallet, roles: ["admin", "encargado", "encargado_comisionista", "repartidor", "comisionista", "punto"] },
  { to: "/app/finanzas", label: "Finanzas", icon: BadgePercent, roles: ["admin"] },
  { to: "/app/credito", label: "Estado de Cuenta", icon: CreditCard, roles: ["admin"] },
  { to: "/app/comisiones", label: "Comisiones y pagos", icon: BadgeDollarSign, roles: ["admin", "encargado", "encargado_comisionista", "comisionista", "punto"] },
  { to: "/app/boleta-ocasional", label: "Crear boleta", icon: PlusCircle, roles: ["admin", "encargado", "encargado_comisionista", "punto", "repartidor", "comisionista"] },
  { to: "/app/almacenamiento", label: "Almacenamiento", icon: PackageOpen, roles: OPS },
  { to: "/app/incidencias", label: "Incidencias", icon: AlertTriangle, roles: OPS },
  { to: "/app/documentos", label: "Documentos", icon: FileText, roles: ADMIN_PUNTO },
  { to: "/app/terminos", label: "Términos y Condiciones", icon: ShieldCheck, roles: ["cliente"] },
  { to: "/app/reportes", label: "Reportes", icon: BarChart3, roles: ["admin"] },
  { to: "/app/configuracion", label: "Configuración", icon: Settings, roles: ["admin"] },
  { to: "/app/integraciones", label: "API / Integraciones", icon: KeyRound, roles: ["admin"] },
  { to: "/app/perfil", label: "Mi perfil", icon: UserRound, roles: "*" },
];

export function roleCanAccess(role, path) {
  const item = ALL_ITEMS.find((i) => i.to === path);
  if (!item) return true;
  return item.roles === "*" || item.roles.includes(role);
}

function itemsForRole(role) {
  return ALL_ITEMS.filter((i) => i.roles === "*" || i.roles.includes(role));
}

// Tema rosa personal para la admin Mabel Bazán
const esRosa = (u) => { const n = (u?.name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase(); return n.includes("mabel") && n.includes("bazan"); };
const THEMES = {
  azul: { base: "bg-[#0A1F44]", border: "border-[#132D5C]", active: "bg-[#132D5C]", hover: "hover:bg-[#132D5C]", card: "bg-[#081734]", btn: "bg-[#132D5C] hover:bg-[#1c3a6e]" },
  rosa: { base: "bg-[#9D174D]", border: "border-[#BE185D]", active: "bg-[#BE185D]", hover: "hover:bg-[#BE185D]", card: "bg-[#831843]", btn: "bg-[#BE185D] hover:bg-[#DB2777]" },
};

export function Sidebar({ mobileOpen, setMobileOpen }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = itemsForRole(user?.role);
  const t = THEMES[esRosa(user) ? "rosa" : "azul"];

  const inner = (
    <div className={`flex flex-col h-full ${t.base} text-slate-200 w-64`} data-testid={esRosa(user) ? "sidebar-tema-rosa" : "sidebar"}>
      <div className={`p-5 border-b ${t.border} flex items-center justify-between`}>
        <Logo />
        <button className="lg:hidden text-slate-400" onClick={() => setMobileOpen(false)} data-testid="sidebar-close-btn">
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto sidebar-scroll px-3 py-4 space-y-1">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${it.to.split("/").pop() || "dashboard"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? `font-semibold text-[#F5A623] ${t.active} shadow-inner`
                    : `font-medium text-slate-200 hover:text-white ${t.hover}`
                }`
              }
            >
              <Icon className="h-[18px] w-[18px]" />
              {it.label}
            </NavLink>
          );
        })}
      </nav>

      <div className={`mt-auto p-4 m-3 rounded-xl ${t.card} border ${t.border}`}>
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-[#3B82F6] text-white flex items-center justify-center font-bold text-sm shrink-0">
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
            <div className="text-xs text-slate-400 truncate">{user?.email}</div>
          </div>
        </div>
        <div className="mt-2 text-[10px] font-bold text-[#F5A623] uppercase tracking-wider">
          {ROLE_LABELS[user?.role] || user?.role}
        </div>
        <button
          onClick={() => { logout(); navigate("/login"); }}
          data-testid="logout-btn"
          className={`mt-3 w-full flex items-center justify-center gap-2 text-xs font-semibold text-slate-200 hover:text-white ${t.btn} rounded-lg py-2 transition-colors`}
        >
          <LogOut className="h-4 w-4" /> Cerrar sesión
        </button>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden lg:block fixed inset-y-0 left-0 z-40">{inner}</aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative z-50 fade-up">{inner}</div>
        </div>
      )}
    </>
  );
}

export function Header({ title, subtitle, onMenu, action }) {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-30 h-16 bg-white/95 backdrop-blur-md border-b border-[#E2E8F0] px-4 sm:px-6 flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <button className="lg:hidden text-[#0A1F44]" onClick={onMenu} data-testid="menu-toggle-btn">
          <Menu className="h-6 w-6" />
        </button>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-[#0A1F44] font-display truncate">{title}</h1>
          {subtitle && <p className="text-xs text-[#64748B] hidden sm:block truncate">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {action}
        <div className="h-9 w-9 rounded-full bg-[#3B82F6] text-white flex items-center justify-center font-bold text-sm shadow-sm ring-2 ring-blue-100">
          {(user?.name || "U").charAt(0).toUpperCase()}
        </div>
      </div>
    </header>
  );
}
